import type { DonationDocument, GiftCertDocument } from 'fundraising-contentful'
import {
  FORM_ID_PATTERN,
  FORM_LANGS,
  FORM_SLUG_PATTERN,
  type FormDocumentV1,
} from 'fundraising-core'
import {
  formsMode,
  isFormsOptIn,
  parseFormApiPath as parseSharedFormApiPath,
  type FormApiRef as BaseFormApiRef,
  type FormsMode,
  type MonthlyView,
  type ResultChoices,
} from 'fundraising-drupal'

/** What the forms route answers for a slug Drupal publishes. */
export interface DrupalDonation {
  /** The form's Drupal id (FORM_ID_PATTERN): the form id in logs and result URLs. */
  formId: string
  /** The document's language; logs and result URLs carry it beside formId. */
  formLang: FormDocumentV1['lang']
  document: DonationDocument
  /** Contentful thank-you entry ids, until result pages move. */
  results: FormDocumentV1['results']
  /** Per result state: the form's own thank-you page, or the Contentful entry to load. */
  resultChoices: ResultChoices
  description: string
  /** The document asks search engines not to index the page. */
  noindex: boolean
}

/** What the forms route answers for a Månadsgivare slug Drupal publishes. */
export interface DrupalMonthly {
  formId: string
  formLang: FormDocumentV1['lang']
  view: MonthlyView
  /** Contentful thank-you entry ids: ok (e-signed), pending (paper), aborted (signing aborted). */
  results: FormDocumentV1['results']
  resultChoices: ResultChoices
}

/** What the forms route answers for a Minnesgåva slug Drupal publishes. */
export interface DrupalMemorial {
  formId: string
  formLang: FormDocumentV1['lang']
  /** Motifs by Mysoft code, amounts, gåvokod and the delivery cut-off, as `mapHonoraryFields`. */
  document: GiftCertDocument
  defaultAmount: number | null
  results: FormDocumentV1['results']
  resultChoices: ResultChoices
  description: string
  noindex: boolean
}

export { monthlyServeGaps } from 'fundraising-drupal'

/** Imported Månadsgivare slugs are lowercased (`REAK` is `reak`), so a link's slug is looked up so. */
export const monthlySlug = (slug: string) => slug.toLowerCase()

export {
  FORMS_OPTIN_PARAM,
  FORMS_OPTIN_VALUE,
  formsMode,
  isFormsOptIn,
  readsDrupal,
  type FormsMode,
} from 'fundraising-drupal'

/** The only slugs the forms route looks up; anything else goes straight to Contentful. */
export const FORM_SLUG = FORM_SLUG_PATTERN

/**
 * Netlify cache tags for a form page, so Drupal can purge it on publish, unpublish or close:
 * `forms` for every form page, plus one per type and slug. An odd slug gets only `forms`.
 */
export const formCacheTags = (
  lang: string,
  slug: string,
  type: 'donation' | 'monthly' | 'memorial' = 'donation',
) =>
  FORM_SLUG.test(slug) && /^[a-z]{2}$/.test(lang) ? `forms,form-${type}-${lang}-${slug}` : 'forms'

/** Request header the form routes' swr cache varies on (nuxt.config routeRules); set per opted-in request. */
export const FORMS_OPTIN_CACHE_HEADER = 'x-forms-optin'

/** The pages that read Drupal forms, and so take `?forms=drupal`. */
const FORM_PAGE_PATH = /^\/(?:gava|manadsgivare|medgivande|minnesgava)(?:\/|$)/

/**
 * Headers for an opted-in form page under `optin` or `on`: never stored by a CDN or browser (it
 * may show a form not yet live, and a staff test must follow each change at once) and never
 * indexed. Null for any other request.
 */
export function optInPageHeaders(
  mode: FormsMode,
  path: string,
  query: Record<string, unknown>,
): Record<string, string> | null {
  if (mode === 'off' || !FORM_PAGE_PATH.test(path) || !isFormsOptIn(query)) return null
  return { 'Cache-Control': 'private, no-store', 'X-Robots-Tag': 'noindex' }
}

/** The form id of a form Contentful serves. */
export const contentfulFormId = (slug: string) => `cf:${slug}`

/** Whether a result URL's form id names a Drupal form (Contentful's are `cf:<slug>`). */
export const isDrupalFormId = (formId: unknown): formId is string =>
  typeof formId === 'string' && FORM_ID_PATTERN.test(formId)

/** Whether a result URL's form language is one the contract has, and so safe in a route path. */
export const isFormLang = (lang: unknown): lang is FormDocumentV1['lang'] =>
  (FORM_LANGS as readonly unknown[]).includes(lang)

/**
 * The Drupal form /manadsgivare handed to /medgivande (`formId`, `formLang` in its link) when this
 * page could not read the same form, else null. The page then reports it and keeps the form's id
 * for the thank-you page, never another form's pages.
 */
export function lostDrupalForm(
  query: Record<string, unknown>,
  readFormId: string | undefined,
  siteLang: string,
): { formId: string; formLang: string } | null {
  const formId = [query.formId].flat()[0]
  const formLang = [query.formLang].flat()[0]
  if (!isDrupalFormId(formId) || readFormId === formId) return null
  return { formId, formLang: isFormLang(formLang) ? formLang : siteLang }
}

/**
 * The fundraising API of a page serving a Drupal Minnesgåva: the flow's `basePath`, so its start
 * names the form and the route applies the form's rules (server/api/fundraising/form). `via` says
 * whether the page's link carried its own `?campaignCode` and `?forms=drupal`. The same layout as
 * primary-site's form API.
 */
export type FormApiRef = BaseFormApiRef<'memorial'>

export { FORM_API_PREFIX, formApiBase } from 'fundraising-drupal'

/** `<type>/<lang>/<slug>/<via>/<route>` (the path after FORM_API_PREFIX), or null when malformed. */
export const parseFormApiPath = (path: string) => parseSharedFormApiPath(path, ['memorial'])
